import { useRef } from "react";

import {
  Chart as ChartJS,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from "chart.js";

import { Bar } from "react-chartjs-2";

import ChartToolbar from "./ChartToolbar";


// Register the Chart.js components required for this bar chart.
ChartJS.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);


function StationEnergyChart({
  data = [],
  chargerType = "Both"
}) {
  const chartRef = useRef(null);


  const AC_COLOUR = "#2e7d32";
  const DC_COLOUR = "#f9a825";


  // Determine which charger types should be displayed.
  const showAC = chargerType === "Both" || chargerType === "AC";
  const showDC = chargerType === "Both" || chargerType === "DC";


  // Keep stations that match the current charger filter and
  // contain a valid average energy value.
  const stationData = Array.isArray(data)
    ? data.filter((row) => {
        const averageEnergy = Number(row.averageEnergy);

        const validType =
          (row.chargerType === "AC" && showAC) ||
          (row.chargerType === "DC" && showDC);

        return (
          validType &&
          !Number.isNaN(averageEnergy)
        );
      })
    : [];


  // Sort stations alphabetically so their position remains predictable.
  stationData.sort((a, b) => {
    const aName =
      a.locationName ||
      a.chargingStationId ||
      "Unknown Location";

    const bName =
      b.locationName ||
      b.chargingStationId ||
      "Unknown Location";

    return aName.localeCompare(bName);
  });


  // Use the location name as the primary label.
  // Include the charger type so AC/DC stations at the same
  // location remain distinguishable.
  const labels = stationData.map((row) => {
    const name =
      row.locationName ||
      row.chargingStationId ||
      "Unknown Location";

    return `${name} (${row.chargerType})`;
  });


  const chartData = {
    labels,
    datasets: [
      {
        label: "Average Energy per Charge",
        data: stationData.map((row) =>
          Number(row.averageEnergy)
        ),
        backgroundColor: stationData.map((row) =>
          row.chargerType === "DC"
            ? DC_COLOUR
            : AC_COLOUR
        ),
        borderColor: stationData.map((row) =>
          row.chargerType === "DC"
            ? DC_COLOUR
            : AC_COLOUR
        ),
        borderWidth: 1,
        barPercentage: 0.9,
        categoryPercentage: 0.85,
        maxBarThickness: 48
      }
    ]
  };


  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    indexAxis: "y",
    scales: {
      x: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Average Energy per Charge (kWh)"
        }
      },
      y: {
        ticks: {
          autoSkip: false
        },
        title: {
          display: true,
          text: "Charging Station"
        }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          title: (items) => {
            if (!items.length) {
              return "";
            }

            return labels[items[0].dataIndex];
          },

          label: (context) => {
            const row =
              stationData[context.dataIndex];

            return [
              `Average energy: ${Number(row.averageEnergy).toFixed(2)} kWh`,
              `Sessions: ${Number(row.sessionCount).toLocaleString()}`
            ];
          }
        }
      }
    }
  };


  return (
    <article className="chart-card">
      <div className="chart-card-header">
        <div className="chart-card-title">
          <h2>
            Average Energy per Charge by Station
          </h2>

          <p>
            Compare average energy delivered across individual
            charging stations.
          </p>
        </div>


        <ChartToolbar
          chartRef={chartRef}
          title="Average Energy per Charge by Station"
          enableZoom={false}
        />
      </div>


      <div className="chart-canvas-container">
        {stationData.length > 0 ? (
          <Bar
            ref={chartRef}
            data={chartData}
            options={options}
          />
        ) : (
          <div className="chart-empty-state">
            No station energy data available for the selected filters.
          </div>
        )}
      </div>
    </article>
  );
}


export default StationEnergyChart;